import { Button, Checkbox, Container, Heading, Input, Label, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { dateTime, money } from "../lib/format"
import { t, tc } from "../lib/i18n"
import { errorMessage, hqApi, type OrderDetail } from "../lib/medusa"

const toAmount = (text: string) => Number(text.replace(",", ".").trim())

/**
 * Refund a site order, in full or in part. A CARD refund is real money back to the customer's card
 * through Stripe (Medusa's own refund, the same as Medusa admin's). A CASH refund is recorded only:
 * hand the cash back yourself. The customer is emailed unless the tick box is off. Opened from the
 * Refund button at the top of the order's page.
 */
export function RefundForm({ order, onClose }: { order: OrderDetail; onClose: () => void }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const left = order.payment.refundable
  const [amount, setAmount] = useState(left.toFixed(2))
  const [reason, setReason] = useState("")
  const [notify, setNotify] = useState(true)
  const card = order.payment.kind === "card"

  const refund = useMutation({
    mutationFn: (value: number) => hqApi.refundOrder(order.id, { amount: value, reason: reason.trim() || null, notify }),
    onSuccess: (body, value) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      toast.success(
        [
          card ? t("{amount} refunded to the card.", { amount: money(value) }) : t("{amount} refunded (recorded).", { amount: money(value) }),
          notify ? t("The customer is being emailed.") : null,
        ]
          .filter(Boolean)
          .join(" ")
      )
      onClose()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const value = toAmount(amount)
  const valid = Number.isFinite(value) && value > 0 && value <= left + 0.001

  async function confirm() {
    if (!valid) return
    const ok = await prompt({
      title: t("Refund {amount} on {ref}?", { amount: money(value), ref: order.ref }),
      description: [
        card
          ? t("{amount} goes back to the customer's card through Stripe. This is real money and cannot be undone.", { amount: money(value) })
          : t("The refund is recorded. Give the {amount} back in cash yourself.", { amount: money(value) }),
        notify ? (card ? t("They are emailed.") : t("The customer is emailed.")) : t("No email is sent."),
      ].join(" "),
      confirmText: t("Refund {amount}", { amount: money(value) }),
      cancelText: t("Back"),
      variant: "danger",
    })
    if (ok) refund.mutate(Math.round(value * 100) / 100)
  }

  return (
    <div className="flex flex-col gap-y-2.5">
      <Text size="xsmall" className="text-ui-fg-subtle">
        {card ? t("Paid by card: the refund goes back to the card through Stripe.") : t("Paid in cash: the refund is recorded and you hand the money back.")} {t("Up to {amount}.", { amount: money(left) })}
      </Text>
      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-y-1">
          <Label htmlFor="refund-amount" className="txt-compact-xsmall">{t("Amount €")}</Label>
          <Input id="refund-amount" inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} aria-invalid={!valid} />
        </div>
        <div className="flex items-end gap-1">
          <Button type="button" size="small" variant="transparent" onClick={() => setAmount(left.toFixed(2))}>{tc("refund", "All")}</Button>
        </div>
      </div>
      <div className="flex flex-col gap-y-1">
        <Label htmlFor="refund-reason" className="txt-compact-xsmall">{t("Reason (for you; shown in Medusa admin too)")}</Label>
        <Input id="refund-reason" maxLength={300} placeholder={t("e.g. Returned, wrong size")} value={reason} onChange={(event) => setReason(event.target.value)} />
      </div>
      <div className="flex items-start gap-x-2">
        <Checkbox id="refund-notify" checked={notify} onCheckedChange={(v) => setNotify(v === true)} className="mt-0.5" />
        <Label htmlFor="refund-notify" className="txt-compact-small">
          {order.email != null ? t("Email {email} about the refund", { email: order.email }) : t("Email the customer about the refund")}
        </Label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="small" variant="danger" isLoading={refund.isPending} disabled={!valid} onClick={() => void confirm()}>
          {valid ? t("Refund {amount}", { amount: money(value) }) : t("Refund")}
        </Button>
        <Button size="small" variant="secondary" onClick={onClose}>{t("Cancel")}</Button>
      </div>
    </div>
  )
}

/** The refunds already made on the order, near the bottom of its page. */
export function RefundPanel({ order }: { order: OrderDetail }) {
  if (order.payment.refunds.length === 0) return null
  return (
    <Container className="flex flex-col gap-y-3">
      <Heading level="h2">{t("Refunds")}</Heading>
      <ul className="flex flex-col gap-y-1">
        {order.payment.refunds.map((r, index) => (
          <li key={r.id ?? index} className="flex items-baseline justify-between gap-x-3">
            <Text size="small">
              {dateTime(r.created_at)}
              {r.note ? ` · ${r.note}` : ""}
              {!r.emailed && <span className="text-ui-fg-muted"> · {t("no email")}</span>}
            </Text>
            <Text size="small" weight="plus" className="shrink-0 whitespace-nowrap">− {money(r.amount)}</Text>
          </li>
        ))}
      </ul>
      {order.payment.refundable <= 0 && <Text size="xsmall" className="text-ui-fg-muted">{t("Everything taken on this order has been refunded.")}</Text>}
    </Container>
  )
}
